import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { ItemReplaceReturnLitigationModal } from '@/types/modal';
import { useCreateItemReturnLitigation, useGetItemReplaceReturnLitigation } from '@/hooks/item';
import { ErrorModal, StandardModal, NoFooterModal, LoadingModal } from '@/components/molecules/common/Modal';
import { Dropdown, Input } from '@/components/molecules/common/Form';
import { Skeleton } from '@mui/material';
import styles from './Modal.module.scss';

const zendeskTicketClosedStatus = 'CLOSED';
const zendeskTicketClosedNoDecisionStatus = 'FAIL_ZENDESK_MERGED_AND_CLOSED';
const View: React.FC<{ modal: ItemReplaceReturnLitigationModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  let title = t('oms_return_status.replace_ticket.title');
  let ignoreSubmitLabel = false;
  let closeLabel = t('common.cancel').toUpperCase();

  const detailR = useGetItemReplaceReturnLitigation(Number(item.productId));
  const sumbitR = useCreateItemReturnLitigation();

  const [isDeliveredStatus, setIsDeliveredStatus] = useState(false.toString());
  useEffect(() => {
    if (detailR.kind === 'Loaded') {
      const prefillData = detailR?.data?.prefillData;
      if (prefillData) {
        setIsDeliveredStatus(prefillData.itemReceived?.toString());
        setReason(prefillData.reason?.toString());
      }
    }
  }, [detailR.kind]);

  if (detailR.kind === 'Called') {
    return (
      <LoadingModal
        title={title}
        onClose={onClose}
      />
    );
  }
  const isDeliveredStatuses = [
    {
      label: t('common.yes'),
      value: true.toString(),
    },
    {
      label: t('common.no'),
      value: false.toString(),
    },
  ];
  let body = <div></div>;

  if (detailR.kind === 'Error') {
    return (
      <ErrorModal
        title={title}
        error={detailR.error}
        onClose={onClose}
      />
    );
  }

  const onSubmit = () => {
    sumbitR.mutate({
      description: description,
      isReplacementTicket: true,
      itemReceived: isDeliveredStatus.toLowerCase() === 'true',
      reason: reason,
      productId: item.productId,
    });
  };

  if (sumbitR.kind === 'Loading') {
    return (
      <NoFooterModal
        title={title}
        body={
          <>
            <Skeleton
              height={20}
              variant="rounded"
            />
            <div className={styles.updateItemAttributeWait}>{t('oms_return_status.replace_ticket.waiting')}</div>
          </>
        }
        onClose={onClose}
        modalName="item_replace_return_litigation"
      />
    );
  }

  if (sumbitR.kind === 'Error') {
    return (
      <ErrorModal
        title={title}
        error={sumbitR.error}
        onClose={onClose}
        containerStyle={{
          width: 400,
        }}
      />
    );
  }

  if (sumbitR.kind === 'Completed') {
    const data = sumbitR.data;

    const refTicketNumber = getRefTicketNumber(
      data.zendeskTicketLink ? data.zendeskTicketLink : '',
      data.zendeskTicketId ? data.zendeskTicketId : '',
    );

    const descriptionContent = (
      <div
        dangerouslySetInnerHTML={{
          __html: t('oms_return_status.replace_ticket.success.description', {
            refTicketNumber: refTicketNumber,
          }),
        }}
      />
    );

    title = t('oms_return_status.replace_ticket.success.title');
    ignoreSubmitLabel = true;
    closeLabel = t('common.close').toUpperCase();

    body = (
      <div className={styles.containerGap}>
        <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
        <div className={styles.text1rem}>{descriptionContent}</div>
      </div>
    );
  } else {
    const data = detailR.data;

    if (
      data.zendeskTicketId != '' &&
      data.zendeskTicketStatus != zendeskTicketClosedStatus &&
      data.zendeskTicketStatus != zendeskTicketClosedNoDecisionStatus
    ) {
      return (
        <StandardModal
          title={title}
          onClose={onClose}
          onSubmit={onSubmit}
          submittable={true}
          ignoreSubmitLabel={true}
          body={
            <div className={styles.containerGap}>
              <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
              <div className={styles.text1rem}>{t('oms_return_status.replace_ticket.exist_ticket')}</div>
            </div>
          }
          closeLabel={t('common.ok').toUpperCase()}
          modalName="replace_ticket.error"
        />
      );
    }

    const itemReceivedReasons = data.itemReceived
      ? data.itemReceived?.map(({ value, displayText }) => ({
          label: displayText == undefined || displayText == null ? '' : displayText,
          value: value == undefined || value == null ? '' : value,
        }))
      : [];

    const itemNotReceivedReasons = data.itemNotReceived
      ? data.itemNotReceived?.map(({ value, displayText }) => ({
          label: displayText == undefined || displayText == null ? '' : displayText,
          value: value == undefined || value == null ? '' : value,
        }))
      : [];

    if (!itemNotReceivedReasons.length) {
      if (isDeliveredStatus !== true.toString()) {
        setIsDeliveredStatus(true.toString());
      }
    }

    const reasons = {
      true: itemReceivedReasons,
      false: itemNotReceivedReasons,
    }; // if buyer received item or not
    const isExistBothItemReasons = !!itemReceivedReasons.length && !!itemNotReceivedReasons.length;

    body = (
      <div className={styles.containerGap}>
        <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
        <div className={clsx([styles.text1rem, styles.textGrey])}>
          {t('oms_return_status.replace_ticket.description')}
        </div>
        {isExistBothItemReasons && (
          <Dropdown
            required={true}
            disabled={false}
            label={t('oms_return_status.replace_ticket.buyer_receive_the_item')}
            value={isDeliveredStatus}
            onSelect={setIsDeliveredStatus}
            items={isDeliveredStatuses}
          />
        )}
        <Dropdown
          required={true}
          disabled={false}
          label={t('oms_return_status.replace_ticket.issue')}
          value={reason}
          onSelect={setReason}
          items={isDeliveredStatus.toLowerCase() === 'true' ? reasons.true : reasons.false}
        />

        <Input
          required={true}
          disabled={false}
          label={t('oms_return_status.replace_ticket.issue_description')}
          value={description}
          multiline={true}
          rows={4}
          onChange={setDescription}
        />
      </div>
    );
  }

  return (
    <StandardModal
      title={title}
      onClose={onClose}
      onSubmit={onSubmit}
      submittable={description != '' && isDeliveredStatus != null && reason != ''}
      body={body}
      containerStyle={{ maxWidth: 650 }}
      submitLabel={t('common.submit').toUpperCase()}
      modalName="replace_ticket"
      ignoreSubmitLabel={ignoreSubmitLabel}
      closeLabel={closeLabel}
    />
  );
};

function getRefTicketNumber(link: string, ticketId: number | string): string {
  return (
    '<a href=' +
    link +
    ' target="_blank" rel="noopener noreferrer" style="text-decoration:underline;color:#1976d2">#' +
    ticketId +
    '</a>'
  );
}

export default View;
